import React from "react";
import CardPayment from "../CardPayment/CardPayment";
import { EMPTY_CARD, useCards } from "../../../contexts/CardsContext";
import { useSelectedResult } from "../../../contexts/SelectedResultContext";
import { formatSequentialIls } from "../../../utils/payment/sequentialMultiCardPayment";

const PaymentSplit = ({
  handlingFee,
  commission,
  priceToPay,
  setPriceToPay,
  setCardPaid,
  setPaymentLinks,
  paymentLinks,
  setPaymentLinksTotalToPay,
  selectedResult,
  paymentTermsAccepted,
  onPaymentTermsBlocked,
  packageProfileCreditMarkers = false,
  creditPaymentApi = "lds",
  splitPaymentError = null,
  afterPayLinkSlot = null,
  beforeCreatePaymentLink = null,
  instantOpenPay = false,
  focusPayLayout = false,
  sequentialMode = false,
  sequentialActiveIndex = 0,
  sequentialSteps = [],
  sequentialAllPaid = false,
  requiredOrderTotalIls = null,
  onSequentialProcessingChange = null,
  onSequentialFailed = null,
  onSequentialSlotPaid = null,
  completeToPublicConfirmation = false,
}) => {
  const { cards, updateCards } = useCards();
  const { ourSavedLDSOrder, setOurSavedLDSOrder } = useSelectedResult();

  const previousPaymentsRef = React.useRef(null);

  React.useEffect(() => {
    if (ourSavedLDSOrder && Array.isArray(ourSavedLDSOrder.payments)) {
      const payments = ourSavedLDSOrder.payments;
      const paymentsChanged =
        JSON.stringify(previousPaymentsRef.current) !== JSON.stringify(payments);

      if (paymentsChanged) {
        previousPaymentsRef.current = payments;
        setPaymentLinks(payments);
        setPaymentLinksTotalToPay(
          payments
            .filter((payment) => payment.paid === true)
            .reduce((acc, cur) => acc + cur.actualAmount, 0)
        );

        if (!sequentialMode && cards.length < payments.length) {
          const tempCards = [...cards];
          for (let i = cards.length; i < payments.length; i++) {
            tempCards.push({ ...EMPTY_CARD, orderReqData: null });
          }
          updateCards(tempCards);
        }
      }
    }
  }, [
    ourSavedLDSOrder?.payments,
    cards.length,
    sequentialMode,
    setPaymentLinks,
    setPaymentLinksTotalToPay,
    updateCards,
  ]);

  const splitClass = [
    "payment-split",
    sequentialMode || cards.length <= 1 ? "payment-split--single" : "payment-split--multi",
    focusPayLayout ? "payment-split--focus" : "",
    sequentialMode ? "payment-split--sequential" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const activeIndex = sequentialMode
    ? Math.max(0, Number(sequentialActiveIndex) || 0)
    : null;

  const renderCard = (index) => {
    const card = cards[index];
    if (!card) return null;
    const { orderReqData } = card || {};
    const step = sequentialSteps[index];
    return (
      <div
        key={`payment-card-${index}`}
        className={`payment-split__card${sequentialMode ? " payment-split__card--sequential" : ""}`}
      >
        {sequentialMode ? (
          <div className="seq-panel" dir="rtl" key={`seq-panel-${index}`}>
            {cards.length > 1 ? (
              <div className="seq-panel__head">
                <p className="seq-panel__kicker">
                  תשלום {index + 1} מתוך {cards.length}
                </p>
                <p className="seq-panel__charge-label">סכום לחיוב</p>
                <p className="seq-panel__charge">
                  {formatSequentialIls(step?.plannedAmount || card.amount)}
                </p>
              </div>
            ) : null}
            <CardPayment
              cardIndex={index}
              orderReqData={orderReqData}
              handlingFee={handlingFee}
              commission={commission}
              priceToPay={priceToPay}
              savePriceToPay={setPriceToPay}
              setCardPaid={setCardPaid}
              paymentLink={paymentLinks[index] || null}
              setOrderReqData={setOurSavedLDSOrder}
              currentOrderId={ourSavedLDSOrder?.id || ""}
              selectedResult={selectedResult}
              paymentTermsAccepted={paymentTermsAccepted}
              onPaymentTermsBlocked={onPaymentTermsBlocked}
              packageProfileCreditMarkers={packageProfileCreditMarkers}
              creditPaymentApi={creditPaymentApi}
              splitPaymentError={splitPaymentError}
              afterPayLinkSlot={null}
              beforeCreatePaymentLink={beforeCreatePaymentLink}
              instantOpenPay={instantOpenPay}
              forceExpanded
              sequentialMode
              requiredOrderTotalIls={requiredOrderTotalIls}
              onSequentialProcessingChange={onSequentialProcessingChange}
              onSequentialFailed={onSequentialFailed}
              onSequentialSlotPaid={onSequentialSlotPaid}
              completeToPublicConfirmation={completeToPublicConfirmation}
            />
          </div>
        ) : (
          <CardPayment
            cardIndex={index}
            orderReqData={orderReqData}
            handlingFee={handlingFee}
            commission={commission}
            priceToPay={priceToPay}
            savePriceToPay={setPriceToPay}
            setCardPaid={setCardPaid}
            paymentLink={paymentLinks[index] || null}
            setOrderReqData={setOurSavedLDSOrder}
            currentOrderId={ourSavedLDSOrder?.id || ""}
            selectedResult={selectedResult}
            paymentTermsAccepted={paymentTermsAccepted}
            onPaymentTermsBlocked={onPaymentTermsBlocked}
            packageProfileCreditMarkers={packageProfileCreditMarkers}
            creditPaymentApi={creditPaymentApi}
            splitPaymentError={splitPaymentError}
            afterPayLinkSlot={null}
            beforeCreatePaymentLink={beforeCreatePaymentLink}
            instantOpenPay={instantOpenPay}
            forceExpanded={focusPayLayout}
          />
        )}
      </div>
    );
  };

  return (
    <div className={splitClass}>
      <div className="payment-split__cards">
        {sequentialMode ? (
          sequentialAllPaid ? (
            <div className="seq-panel seq-panel__complete" dir="rtl">
              כל התשלומים התקבלו. מאמתים מול השרת…
            </div>
          ) : (
            renderCard(activeIndex)
          )
        ) : (
          cards.map((_, index) => renderCard(index))
        )}
      </div>
      {afterPayLinkSlot ? (
        <div className="payment-split__after">{afterPayLinkSlot}</div>
      ) : null}
    </div>
  );
};

export default PaymentSplit;
